import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Flame, Pickaxe, HardHat, Zap, Plus, ChevronRight, Shield, ClipboardList,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState } from "@/components/ui";
import { fmtDateTime } from "@/lib/dates";

// =============================================================================
// Permits screen — list + quick issue
// =============================================================================

const PERMIT_META: Record<string, { label: string; icon: any; accent: string }> = {
  hot_work:            { label: "Hot Work",           icon: Flame,    accent: "text-danger" },
  excavation:          { label: "Excavation",         icon: Pickaxe,  accent: "text-warn" },
  workbox:             { label: "Workbox / EWP",      icon: HardHat,  accent: "text-info" },
  confined_space:      { label: "Confined Space",     icon: Shield,   accent: "text-warn" },
  working_at_heights:  { label: "Working at Heights", icon: HardHat,  accent: "text-info" },
  live_electrical:     { label: "Live Electrical",    icon: Zap,      accent: "text-danger" },
};

export default function PermitsScreen() {
  const user = useRequireAuth();
  const [filter, setFilter] = useState<"active" | "all" | "closed">("active");
  const [issueMenu, setIssueMenu] = useState(false);

  const { data: permits = [], isLoading } = useQuery({
    queryKey: ["permits", filter],
    queryFn: async () => {
      let q = supabase
        .from("permits")
        .select(`
          permit_id, permit_number, permit_type, status,
          scope_description, valid_from, valid_until,
          projects(project_code, project_name),
          sites(site_code, site_name)
        `)
        .order("valid_from", { ascending: false })
        .limit(200);
      if (filter === "active") q = q.in("status", ["issued", "active"]);
      if (filter === "closed") q = q.eq("status", "closed");
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
  });

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Permits</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Hot work, excavation, confined space, heights, EWP, live electrical.
          </div>
        </div>
        <div className="relative">
          <Button icon={Plus} onClick={() => setIssueMenu(m => !m)}>Issue permit</Button>
          {issueMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setIssueMenu(false)} />
              <Card className="absolute right-0 top-full mt-2 min-w-[240px] z-20">
                {Object.entries(PERMIT_META).map(([key, meta]) => {
                  const Icon = meta.icon;
                  return (
                    <Link
                      key={key}
                      to={`/permits/issue/${key}`}
                      onClick={() => setIssueMenu(false)}
                      className="flex items-center gap-3 px-4 py-3 border-b border-line last:border-b-0 hover:bg-ink-hover"
                    >
                      <Icon size={15} className={meta.accent} />
                      <span className="text-[13px] text-text">{meta.label}</span>
                    </Link>
                  );
                })}
              </Card>
            </>
          )}
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 mb-4">
        {(["active", "all", "closed"] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider2 font-semibold border ${
              filter === f ? "bg-accent/12 text-accent border-accent/30" : "bg-ink-raised text-text-muted border-line"
            }`}
          >{f}</button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner size={18} /></div>
      ) : permits.length === 0 ? (
        <Card><EmptyState title="No permits" description="Issue a new one to get started." icon={ClipboardList} /></Card>
      ) : (
        <div className="space-y-2">
          {permits.map(p => {
            const meta = PERMIT_META[p.permit_type] ?? { label: p.permit_type, icon: ClipboardList, accent: "text-text-muted" };
            const Icon = meta.icon;
            return (
              <Link key={p.permit_id} to={`/permits/${p.permit_id}`}>
                <Card>
                  <div className="p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-md bg-ink-raised border border-line flex items-center justify-center shrink-0 ${meta.accent}`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="font-mono text-[11px] text-accent">{p.permit_number}</span>
                        <span className="text-[13px] text-text">{meta.label}</span>
                        <Pill label={p.status} tone={p.status === "active" ? "live" : p.status === "closed" ? "closed" : "mobilising"} small />
                      </div>
                      <div className="text-[12px] text-text-muted truncate">{p.scope_description}</div>
                      <div className="text-[11px] font-mono text-text-muted mt-0.5">
                        {p.projects?.project_code ?? "—"} · {p.sites?.site_code ?? "—"} · valid {fmtDateTime(p.valid_from)} → {fmtDateTime(p.valid_until)}
                      </div>
                    </div>
                    <ChevronRight size={14} className="text-text-dim shrink-0" />
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
